import { useState, createRef, useContext, useRef, useEffect } from "react";
import axios from "axios";
import parse from "html-react-parser";
import uuid from "react-uuid";

// hooks
import useToggleState from "../../hooks/useToggleState";
import { useFormDonationType } from "../../hooks/useForm";
import { useFormDelivery } from "../../hooks/useForm";

// functions
import { findGlobalContent } from "../../functions/findGlobalContent";
import { formatDate, formatNumberString } from "../../functions/formatValue";
import {
  AppdataContext,
  AppdataDispatchContext,
} from "../../functions/appdataContext";
import { initiateSignBankId } from "../../functions/billectaCalls";
import { saveLead } from "../../functions/leadCalls";
import { getErrorMessage } from "../../functions/getErrorMessage";
import { inputValidation } from "../../functions/inputValidation";

// components
import Button from "../Button/Button";
import CertificateDeliveryForm from "./components/CertificateDeliveryForm/CertificateDeliveryForm";
import CompanyDetailsForm from "./components/CompanyDetailsForm";
import CreateCertificateForm from "./components/CreateCertificateForm/CreateCertificateForm";
import DonationAmountForm from "./components/DonationAmountForm";
import DonationTypeForm from "./components/DonationTypeForm";
import Icon from "../Icon/Icon";
import PersonalDetailsForm from "./components/PersonalDetailsForm/PersonalDetailsForm";
import RecurringPaymentForm from "./components/RecurringPaymentForm/RecurringPaymentForm";
import SinglePaymentForm from "./components/SinglePaymentForm";
import Alert from "../Alert/Alert";
import Repejo from "./components/Repejo/Repejo";

// styles
import styles from "./Form.module.scss";

async function getSha256(value) {
  if (
    typeof window !== "undefined" &&
    window.crypto &&
    window.crypto.subtle &&
    window.crypto.subtle.digest
  ) {
    const msgUint8 = new TextEncoder().encode(value);
    const hashBuffer = await crypto.subtle.digest("SHA-256", msgUint8);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    return hashHex;
  }
}

const Form = () => {
  const appdata = useContext(AppdataContext);
  const dispatch = useContext(AppdataDispatchContext);
  const { initialServerData, formData, formErrors, formSuccess } = appdata;
  const { amounts, global: globalData } = initialServerData || {};
  const [isOpenMemorialPreview, toggleOpenMemorialPreview] = useToggleState(
    false,
  );
  const [isOpenTributePreview, toggleOpenTributePreview] = useToggleState(
    false,
  );
  const [
    manualPersonalDetailsFormIsOpen,
    setManualPersonalDetailsFormIsOpen,
  ] = useState(false);
  const [
    manualBankAccountFormIsOpen,
    setManualBankAccountFormIsOpen,
  ] = useState(false);
  const [customAmount, setCustomAmount] = useState(
    formData?.customAmount || "",
  );
  const [lastInteraction, setLastInteraction] = useState(undefined);
  const [showInputErrorAlert, setShowInputErrorAlert] = useState(false);
  const donationType = initialServerData?.donation_type;
  const isMonthlyDonation = formData && formData?.donation_type === "monthly";
  const donationAmountRef = createRef();
  const greetingsTextRef = createRef();
  const submitButtonRef = useRef(null);
  const stateRef = useRef();
  stateRef.current = appdata;

  // console.log("Form", formData);

  // Reference of formdata to only update lead on form change
  const previousFormDataRef = useRef(formData);

  const memorialDefaultGreeting = `Till minne av\n${formData?.honoree_name}\n\n`;

  // global content keys
  const keys = {
    submitButton: isMonthlyDonation
      ? "button_registerMonthlyDonor"
      : "button_toPayment",
    privacyPolicy: "richtext_privacyPolicy",
  };

  // default values
  const texts = {
    submitButton: isMonthlyDonation ? "Bli månadsgivare" : "Till betalning",
  };

  const repejoShortLink = initialServerData?.repejo_short_link ?? null;
  const repejoTitle = initialServerData?.repejo_title ?? null;
  const repejoText = initialServerData?.repejo_text ?? null;
  const repejoBaseUrl = initialServerData?.repejo_base_url ?? null;
  const thank_you_url = initialServerData?.thank_you_url ?? null;

  const submitButtonProps = {
    globalData,
    key: keys.submitButton,
    defaultValue: texts.submitButton,
  };

  const showMonthlyGiftAlert = findGlobalContent({
    globalData,
    key: "toggle_showMonthlyGiftAlert",
    defaultValue: false,
  });

  const onChangeInput = (e) => {
    const { name, value } = e.target;

    const maxLength =
      name === "phone"
        ? 25
        : name === "socialsecuritynumber"
        ? 12
        : name === "burial_date" || name === "orgnumber"
        ? 10
        : name === "clearing" || name === "zip" || name === "recipient_zip"
        ? 5
        : null;

    const inputValue =
      name === "phone" ||
      name === "account" ||
      name === "clearing" ||
      name === "orgnumber" ||
      name === "zip" ||
      name === "recipient_zip" ||
      name === "socialsecuritynumber"
        ? formatNumberString(value)
        : name === "burial_date"
        ? formatDate(value)
        : value;

    if (maxLength) {
      if (inputValue.length <= maxLength) {
        dispatch({
          type: "setFormValue",
          value: { name, value: inputValue },
        });
      }
    } else {
      dispatch({
        type: "setFormValue",
        value: { name, value: inputValue },
      });
    }
  };

  const clearRelatedFormResponseOnFormChange = (name, value) => {
    if (name === "donation_type") {
      if (value === "monthly") {
        clearFormResponse("method");
      } else if (value === "donation") {
        const keys = ["account", "clearing", "bank", "autogiro_optIn"];
        keys.forEach((name) => clearFormResponse(name));
      }
    }

    if (name === "delivery_method") {
      const keys = [
        "recipient_email",
        "recipient_firstname",
        "recipient_lastname",
        "recipient_address",
        "recipient_zip",
        "recipient_city",
      ];
      keys.forEach((name) => clearFormResponse(name));
    }

    if (name === "delivery_recipient") {
      const keys = [
        "recipient_email",
        "recipient_firstname",
        "recipient_lastname",
        "recipient_address",
        "recipient_zip",
        "recipient_city",
      ];
      keys.forEach((name) => clearFormResponse(name));
    }
  };

  const onChangeInputRadioOrCheckbox = (name, value) => {
    clearFormResponse(name);

    dispatch({
      type: "setFormValue",
      value: { name, value },
    });

    if (
      name === "amount" &&
      (value === amounts[0]?.amount ||
        value === amounts[1]?.amount ||
        value === amounts[2]?.amount)
    ) {
      setCustomAmount("");
    }

    if (name === "delivery_method" && formData?.delivery_recipient) {
      dispatch({
        type: "setFormValue",
        value: { name: "delivery_recipient", value: "" },
      });
    }

    clearRelatedFormResponseOnFormChange(name, value);

    // TODO - create better method for assuring data has been saved before triggering saveLead
    setTimeout(() => {
      triggerSaveLead();
    }, 500);
  };

  const clearFormError = (name) => {
    if (formErrors[name]) {
      dispatch({
        type: "setFormErrorValue",
        value: { name, value: "" },
      });
    }
  };

  const clearFormSuccess = (name) => {
    if (formSuccess[name]) {
      dispatch({
        type: "setFormSuccessValue",
        value: { name, value: false },
      });
    }
  };

  const clearFormResponse = (name) => {
    clearFormError(name);
    clearFormSuccess(name);
  };

  const handleInputFocus = (e) => {
    const { name, id } = e.target;
    clearFormResponse(name);
    setLastInteraction(id || name);
  };

  const handleInputValidation = (e) => {
    const { name, value } = e.target;

    const isValid = inputValidation({ name, value });

    if (isValid) {
      dispatch({
        type: "setFormSuccessValue",
        value: { name, value: true },
      });
    } else {
      dispatch({
        type: "setFormErrorValue",
        value: { name, value: getErrorMessage(name) },
      });
    }
  };

  const triggerSaveLead = () => saveLead(stateRef, dispatch);

  const handleInputBlur = (e) => {
    const { name, value } = e.target;
    const previousValue = previousFormDataRef.current[name];

    // Check that value has been changed and that value isn't empty

    if (previousValue !== value && value !== "") {
      // Check the field is not socialsecuritynumber as the triggerSaveLead is called on getAddress() in PersonalDetailsForm instead
      if (name !== "socialsecuritynumber") {
        triggerSaveLead();
      }
      previousFormDataRef.current = formData;
    }

    if (formData[name]) handleInputValidation(e);
  };

  const handlePreviewButton = async (action) => {
    if (donationType === "memorial") toggleOpenMemorialPreview();
    if (donationType === "tribute") toggleOpenTributePreview();

    const focusElement =
      action === "edit" ? greetingsTextRef.current : donationAmountRef.current;

    await new Promise((res) => setTimeout(res, 1));

    focusElement.focus();
  };

  // manage keys in formData for donation_type "monthly" & "donation"
  useFormDonationType();

  // manage keys in formData for delivery_method & delivery_recipient
  useFormDelivery();

  const canSubmit =
    formData?.donation_type !== "default" && // donation type is set
    Object.values(formData).every((x) => x !== "") && // no missing values
    Object.values(formErrors).every((x) => x === "") && // no errors
    (formData.autogiro_optIn === undefined || formData.autogiro_optIn === true); // autogiro_optIn must be true if it exists

  // WP: Submit form
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (canSubmit) {
      try {
        const email = formData.email?.toLowerCase();
        const emailData = email
          ? {
              email: email,
              email_sha256: await getSha256(email),
            }
          : {
              email: null,
              email_sha256: null,
            };
        const phoneWithoutPlus = formData.phone
          ?.replace(/\D/g, "")
          .replace(/^0/, "46");
        const phoneWithPlus =
          phoneWithoutPlus && phoneWithoutPlus !== ""
            ? `+${phoneWithoutPlus}`
            : null;
        const phoneData = phoneWithPlus
          ? {
              phone_number_plus: phoneWithPlus,
              phone_number_plus_sha256: await getSha256(phoneWithPlus),
              phone_number_noplus: phoneWithoutPlus,
              phone_number_noplus_sha256: await getSha256(phoneWithoutPlus),
            }
          : {
              phone_number_plus: null,
              phone_number_plus_sha256: null,
              phone_number_noplus: null,
              phone_number_noplus_sha256: null,
            };
        if (window.dataLayer) {
          window.dataLayer.push({
            event: "donationFormSubmit",
            ...emailData,
            ...phoneData,
            data: appdata,
          });
        }
      } catch (error) {
        console.error(error);
      }

      isMonthlyDonation ? submitMonthlyForm(event) : submitRegularForm(event);
    } else {
      if (formData?.donation_type !== "company")
        setManualPersonalDetailsFormIsOpen(true);

      if (isMonthlyDonation) setManualBankAccountFormIsOpen(true);

      if (formData?.donation_type === "default") {
        dispatch({
          type: "setFormErrorValue",
          value: { name: "donation_type", value: "Fältet är obligatoriskt" },
        });
      }

      const formDataKeys = Object.keys(formData);
      const formDataValues = Object.values(formData);

      formDataValues.forEach((value, index) => {
        if (value === "" || value === false) {
          const name = formDataKeys[index];

          dispatch({
            type: "setFormErrorValue",
            value: { name, value: "Fältet är obligatoriskt" },
          });
        }
      });

      setShowInputErrorAlert(true);
      submitButtonRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  // retry submit monthly payment programmatically
  const handleRetrySubmit = () => {
    if (submitButtonRef.current) {
      submitButtonRef.current.click();
    }
  };

  const submitMonthlyForm = async (event) => {
    console.log("submit monthly form");
    initiateSignBankId(event.target, stateRef, dispatch);
  };

  const submitRegularForm = (event) => {
    console.log("submit regular form");
    const { ajax_url, nonce, post_id } = appdata?.backendSetup || {};

    dispatch({
      type: "setSubmitIsLoading",
      value: true,
    });

    // Create session id to facillitate (secure) data population on cancelled payment.
    const session_id = initialServerData?.lead?.session_id
      ? initialServerData.lead.session_id
      : uuid();

    let form_data = new FormData(event.target);
    form_data.set("action", "ktk_donations_initiate_payment");
    form_data.set("nonce", nonce);
    form_data.set("form_id", post_id);
    form_data.set("session_id", session_id);
    form_data.set("donation_type", formData.donation_type);
    form_data.set("campaign_id", initialServerData.campaign_id);
    form_data.set(
      "cancelUrl",
      `${initialServerData.current_url}?id=${formData.lead_id}&session=${session_id}`,
    );
    form_data.set("returnUrl", initialServerData.thank_you_url);

    for (var key in formData) {
      form_data.set(key, formData[key]);
    }

    if (formData?.selected_card) {
      form_data.set(
        "card",
        JSON.stringify(
          initialServerData.pdf_array.filter(
            (element) => element.name === formData.selected_card,
          ),
        ),
      );
    }

    if (formData?.socialsecuritynumber) {
      form_data.set("socialsecuritynumber", formData.socialsecuritynumber);
    }

    if (formData?.card_text && formData?.donation_type === "memorial") {
      const memorialCardText = memorialDefaultGreeting.concat(
        formData?.card_text,
      );
      form_data.set("card_text", memorialCardText);
    }

    axios
      .post(ajax_url, form_data)
      .then((res) => {
        //console.log(res);
        // if success
        if (res.data.href) {
          window.location.href = res.data.href;
        }
        // if error
        if (res.data.error) {
          // Loop through all the fields with errors
          for (var index in res.data.fields) {
            dispatch({
              type: "setFormErrorValue",
              value: {
                name: res.data.fields[index],
                value: getErrorMessage(res.data.fields[index]),
              },
            });
          }
        }
        dispatch({
          type: "setSubmitIsLoading",
          value: false,
        });
      })
      .catch((err) => {
        //console.log(err);

        setTimeout(() => {
          dispatch({
            type: "setSubmitIsLoading",
            value: false,
          });
        }, 1000);
      });
  };

  useEffect(() => {
    const handleFieldInteraction = (event) => {
      const { name, id } = event.target;
      if (id || name) setLastInteraction(id || name);
    };

    const formFields = document.querySelectorAll(
      "form textarea, form select, form button",
    ); // for input, see handleInputFocus

    formFields.forEach((field) => {
      field.addEventListener("click", handleFieldInteraction);
      field.addEventListener("focus", handleFieldInteraction);
    });

    return () => {
      formFields.forEach((field) => {
        field.removeEventListener("click", handleFieldInteraction);
        field.removeEventListener("focus", handleFieldInteraction);
      });
    };
  }, []);

  useEffect(() => {
    if (lastInteraction && window.dataLayer) {
      const handleBeforeUnload = () => {
        window.dataLayer.push({
          event: "formAbandonment",
          eventCategory: "Form abandonment",
          eventAction: "A gift form page was abandoned",
          lastInteractionValue: lastInteraction,
        });
      };

      window.addEventListener("beforeunload", handleBeforeUnload);

      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }
  }, [lastInteraction]);

  useEffect(() => {
    if (canSubmit && showInputErrorAlert) setShowInputErrorAlert(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSubmit]);

  // Blur target instead of form submission on enter except for textarea
  const handleKeyDown = (event) => {
    if (event.key === "Enter" && event.target.tagName !== "TEXTAREA") {
      event.preventDefault();
      event.target.blur();
    }
  };

  const common = {
    globalData,
    donationType,
    onChangeInput,
    onChangeInputRadioOrCheckbox,
    handleInputFocus,
    handleInputBlur,
    handleInputValidation,
  };

  // console.log("Form render", {
  //   donationType,
  //   isMonthlyDonation,
  //   repejoShortLink,
  // });

  return (
    <form
      id="#form"
      onSubmit={handleSubmit}
      className={styles["form"]}
      onKeyDown={handleKeyDown}
    >
      {isMonthlyDonation && repejoShortLink && donationType === "monthly" ? (
        <Repejo
          title={repejoTitle}
          text={repejoText}
          shortcode={repejoShortLink}
          thank_you_url={thank_you_url}
          base_url={repejoBaseUrl}
        />
      ) : (
        <>
          {Boolean(
            donationType === "memorial" || donationType === "tribute",
          ) && (
            <CreateCertificateForm
              {...common}
              titlePrefixNumber="1"
              isOpenMemorialPreview={isOpenMemorialPreview}
              toggleOpenMemorialPreview={toggleOpenMemorialPreview}
              isOpenTributePreview={isOpenTributePreview}
              toggleOpenTributePreview={toggleOpenTributePreview}
              pdfArray={initialServerData?.pdf_array}
              handlePreviewButton={handlePreviewButton}
              ref={greetingsTextRef}
              memorialDefaultGreeting={memorialDefaultGreeting}
            />
          )}

          {initialServerData?.amounts && (
            <DonationAmountForm
              {...common}
              titlePrefixNumber={
                Boolean(
                  donationType === "default" ||
                    donationType === "company" ||
                    donationType === "monthly",
                )
                  ? "1"
                  : "2"
              }
              ref={donationAmountRef}
              amounts={amounts}
              amount={formData?.amount}
              customAmount={customAmount}
              setCustomAmount={setCustomAmount}
              disabled={donationType === "default" && isMonthlyDonation}
            />
          )}

          {donationType === "default" && (
            <DonationTypeForm
              {...common}
              titlePrefixNumber="2"
              donationType={formData?.donation_type}
            />
          )}

          {donationType === "default" && isMonthlyDonation ? (
            <Repejo
              title={repejoTitle}
              text={repejoText}
              shortcode={repejoShortLink}
              thank_you_url={thank_you_url}
              base_url={repejoBaseUrl}
            />
          ) : (
            <>
              {donationType === "company" ? (
                <CompanyDetailsForm {...common} titlePrefixNumber="2" />
              ) : (
                <PersonalDetailsForm
                  {...common}
                  titlePrefixNumber={donationType === "monthly" ? "2" : "3"}
                  manualPersonalDetailsFormIsOpen={
                    manualPersonalDetailsFormIsOpen
                  }
                  setManualPersonalDetailsFormIsOpen={
                    setManualPersonalDetailsFormIsOpen
                  }
                  triggerSaveLead={triggerSaveLead}
                  clearFormResponse={clearFormResponse}
                />
              )}

              {(donationType === "memorial" || donationType === "tribute") && (
                <CertificateDeliveryForm {...common} titlePrefixNumber="4" />
              )}

              {!isMonthlyDonation && (
                <SinglePaymentForm
                  {...common}
                  titlePrefixNumber={
                    donationType === "company"
                      ? "3"
                      : donationType === "default"
                      ? "4"
                      : "5"
                  }
                />
              )}

              {isMonthlyDonation && showMonthlyGiftAlert && (
                <Alert
                  text={findGlobalContent({
                    globalData,
                    key: "text_monthlyGiftAlert",
                  })}
                />
              )}

              {isMonthlyDonation && (
                <RecurringPaymentForm
                  {...common}
                  titlePrefixNumber={
                    Boolean(
                      donationType === "monthly" || donationType === "company",
                    )
                      ? "3"
                      : donationType === "default"
                      ? "4"
                      : "5"
                  }
                  manualBankAccountFormIsOpen={manualBankAccountFormIsOpen}
                  setManualBankAccountFormIsOpen={
                    setManualBankAccountFormIsOpen
                  }
                  handleRetrySubmit={handleRetrySubmit}
                />
              )}

              {appdata?.submitIsLoading ? (
                <Button disabled>
                  <Icon variant="loader" size="medium" />
                </Button>
              ) : (
                <Button
                  type="submit"
                  ref={submitButtonRef}
                  value={findGlobalContent(submitButtonProps)}
                  id="form-submit-button"
                  isBankIdButton={isMonthlyDonation}
                >
                  {findGlobalContent(submitButtonProps)}
                </Button>
              )}

              {Boolean(showInputErrorAlert && !canSubmit) && (
                <Alert
                  variant="red-alert"
                  text={findGlobalContent({
                    globalData,
                    key: "text_inputErrorAlert",
                    defaultValue:
                      "<p>Något gick fel. Det verkar som ett eller flera fält är felaktigt ifyllda. <strong>Vänligen kontrollera dina uppgifter och försök igen.</strong> Fält som måste korrigeras är markerade.</p>",
                  })}
                />
              )}
            </>
          )}

          <div className={styles["privacy-policy-richtext"]}>
            {parse(findGlobalContent({ globalData, key: keys.privacyPolicy }))}
          </div>
        </>
      )}
    </form>
  );
};

export default Form;
